import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";

/** Whose unsubscribe link it is, which list it leaves, and whether they are on it. */
export type EmailPreferences = {
  email: string;
  first_name: string;
  kind: "announcements" | "newsletter";
  subscribed: boolean;
  /** The announcement setting, kept under its old name. */
  announcements: boolean;
};

const endpoint = (token: string) => `/email/unsubscribe/${encodeURIComponent(token)}/`;

/**
 * The person an unsubscribe link names, read without a sign-in: the link's
 * token is all that identifies them. A link that names nobody answers 404,
 * which is not worth retrying.
 */
export function useEmailPreferences(token: string) {
  return useQuery({
    queryKey: ["emailPreferences", token],
    queryFn: (): Promise<EmailPreferences> =>
      apiRequest(endpoint(token), "GET", undefined, false),
    retry: false,
  });
}

/**
 * Acts on the link: turns announcements off or back on, or leaves the
 * newsletter, which the token decides rather than the caller. The newsletter
 * has no way back, so the page does not offer one.
 */
export function useSetAnnouncements(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (announcements: boolean): Promise<EmailPreferences> =>
      apiRequest(endpoint(token), "POST", { announcements }, false),
    onSuccess: (data) => queryClient.setQueryData(["emailPreferences", token], data),
  });
}
